import ConstantProperties from './constantProperties.js';
import Events from './events.js';
import People from './people.js';
import Batcher from './batcher.js';
const STORAGEKEY = '__or_sdk_analytics_token';
export default class Analytics {
    /**
     * @param localStorage Class or Object that implements Storage-like interface that stores
     * values persistently like window.localStorage or any other file-based storage
     *
     * @param sessionStorage Class or Object that implements Storage-like interface that stores values
     * on per-session basis like window.sessionStorage or any other in-memory storage
     *
     * @param getToken Function that returns token to bind events to a session
     *
     * @param getTimestamp returns current timestamp
     *
     * @param setUserId callback for people.identify
     *
     * @param standalone if true, analytics will manage its own token (instead of using with openreplay tracker session)
     * */
    constructor(options) {
        this.token = null;
        this.standalone = false;
        this._getToken = () => {
            if (this.standalone) {
                return this.token;
            }
            else {
                return this.getToken();
            }
        };
        this._getTimestamp = () => {
            if (this.standalone) {
                return Date.now();
            }
            return this.getTimestamp();
        };
        this.init = async () => {
            if (!this.standalone) {
                this.batcher.startAutosend();
                return;
            }
            else {
                const defaultFields = this.constantProperties.all;
                const apiEdp = '/v1/sdk/start';
                const data = {
                    projectKey: this.projectKey,
                    defaultFields,
                };
                const resp = await fetch(apiEdp, {
                    method: 'POST',
                    body: JSON.stringify(data),
                });
                if (!resp.ok) {
                    throw new Error(`HTTP error! status: ${resp.status}`);
                }
                const result = await resp.json();
                if (result.token) {
                    this.token = result.token;
                    this.sessionStorage.setItem(STORAGEKEY, result.token);
                }
                else {
                    throw new Error('No token received from server');
                }
            }
        };
        /**
         * Used by tracker when running in blundled mode
         */
        this.onStart = () => {
            if (!this.standalone) {
                this.batcher.restart();
            }
        };
        this.onStop = () => {
            if (!this.standalone) {
                this.batcher.stop();
            }
        };
        this.reset = () => {
            this.people.reset(true);
            this.events.reset();
            this.batcher.stop();
            if (this.standalone) {
                this.token = null;
                this.sessionStorage.setItem(STORAGEKEY, '');
            }
        };
        /**
         * COMPATIBILITY LAYER
         * */
        /**
         * Identify a user with an id (e.g. email, username, etc.)
         * will bind all events and properties (including device_id) to this user
         *
         * you will need to manually call people.reset() to clear the id on logout event
         * */
        this.identify = (user_id) => {
            return this.people.identify(user_id);
        };
        /**
         * Add event to batch with option to send it immediately,
         * properties are optional and will not be saved as super prop
         * */
        this.track = (eventName, properties, options) => {
            return this.events.track(eventName, properties, options);
        };
        this.sessionStorage = options.sessionStorage || sessionStorage;
        this.localStorage = options.localStorage || localStorage;
        this.backendUrl = options.ingestPoint;
        this.projectKey = options.projectKey;
        this.getToken = options.getToken || (() => '');
        this.getTimestamp = options.getTimestamp || (() => Date.now());
        this.setUserId = options.setUserId || (() => { });
        this.standalone = !options.notStandalone;
        this.token = this.sessionStorage.getItem(STORAGEKEY);
        this.constantProperties = new ConstantProperties(this.localStorage, this.sessionStorage);
        this.batcher = new Batcher(this.backendUrl, this._getToken, this.init, this.standalone);
        this.events = new Events(this.constantProperties, this._getTimestamp, this.batcher);
        this.people = new People(this.constantProperties, this._getTimestamp, this.setUserId, this.batcher);
        if (options.notStandalone) {
            this.init();
        }
    }
}
